'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { signIn, useSession } from 'next-auth/react'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { Card, CardContent, Fade, Slide, Zoom, useTheme, alpha, Container, Grid, Paper } from '@mui/material'
import axios from 'axios'
import LogoLogin from '@components/layout/shared/LogoLogin'
import showToastAlert from '@components/Toast'
import themeConfig from '@configs/themeConfig'
import CustomButton from '@components/layout/shared/CustomButton'
import dayjs from 'dayjs'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'

const Login = () => {
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const router = useRouter()
  const { data: session, status } = useSession()
  const theme = useTheme()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [mounted, setMounted] = useState(false)

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (status === 'authenticated' && session?.user) {
      const redirectTo = '/home'
      router.push(redirectTo)
    }
  }, [status, session, router])

  const doLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!username) {
      showToastAlert('info', 'Username tidak boleh kosong')
      return
    }

    if (!password) {
      showToastAlert('info', 'Password tidak boleh kosong')
      return
    }

    setLoading(true)
    await clearAllCookies()

    try {
      const response = await signIn('credentials', {
        redirect: false,
        username: username,
        password: password
      })

      if (response?.error) {
        showToastAlert('info', response.error)
      } else {
        console.log('Login successful, waiting for session update...')
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const clearAllCookies = async () => {
    const cookies = document.cookie.split('; ')

    cookies.forEach(cookie => {
      const [name] = cookie.split('=')
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`
    })

    console.log('Client-side cookies cleared!')

    try {
      await axios.get('/api/clear-cookies')
      console.log('Server-side cookies cleared!')
    } catch (error) {
      console.error('Failed to clear server-side cookies:', error)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        background: `linear-gradient(135deg,
          ${alpha(theme.palette.primary.main, 0.08)} 0%,
          ${alpha(theme.palette.secondary.main, 0.04)} 25%,
          ${alpha(theme.palette.primary.light, 0.06)} 50%,
          ${alpha(theme.palette.secondary.light, 0.02)} 75%,
          ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${theme.palette.primary.main.slice(1)}' fill-opacity='0.02'%3E%3Cpath d='M40 40c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm20 0c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          zIndex: 0
        }
      }}
    >
      <Container maxWidth='xl' sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={0} sx={{ minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
          <Grid item xs={12} sm={10} md={8} lg={10} xl={8}>
            <Fade in={mounted} timeout={800}>
              <Card
                sx={{
                  borderRadius: { xs: 2, sm: 3, md: 4, lg: 5 },
                  boxShadow: `0 32px 64px ${alpha(theme.palette.common.black, 0.08)}, 0 16px 32px ${alpha(theme.palette.common.black, 0.04)}`,
                  overflow: 'hidden',
                  backdropFilter: 'blur(24px)',
                  background: alpha(theme.palette.background.paper, 0.98),
                  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  minHeight: { xs: 'auto', md: '600px', lg: '650px' },
                  height: '100%' // Tambahkan ini
                }}
              >
                <CardContent sx={{ p: 0, height: '100%' }}>
                  <Grid container sx={{ minHeight: { xs: 'auto', md: '600px', lg: '650px' } }}>
                    {/* Left Side - Hero Section */}
                    <Grid item xs={12} md={6} lg={7}>
                      <Slide direction='right' in={mounted} timeout={1000}>
                        <Box
                          sx={{
                            height: '100%',
                            minHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 3, sm: 4, md: 5, lg: 6 },
                            position: 'relative',
                            background: `linear-gradient(135deg,
                              ${alpha(theme.palette.primary.main, 0.06)} 0%,
                              ${alpha(theme.palette.secondary.main, 0.04)} 50%,
                              ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: -100,
                              right: -100,
                              width: 250,
                              height: 250,
                              borderRadius: '50%',
                              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                              animation: 'float 8s ease-in-out infinite'
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: -80,
                              left: -80,
                              width: 200,
                              height: 200,
                              borderRadius: '50%',
                              background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 70%)`,
                              animation: 'float 10s ease-in-out infinite reverse'
                            },
                            '@keyframes float': {
                              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                              '50%': { transform: 'translateY(-30px) rotate(180deg)' }
                            }
                          }}
                        >
                          {/* Hero Content */}
                          <Box sx={{ textAlign: 'center', mb: 4, zIndex: 2 }}>
                            <Typography
                              variant='h3'
                              sx={{
                                fontWeight: 800,
                                mb: 2,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem', lg: '3rem' }
                              }}
                            >
                              Selamat Datang
                            </Typography>
                            <Typography
                              variant='h6'
                              sx={{
                                color: 'text.secondary',
                                mb: 3,
                                fontWeight: 400,
                                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
                              }}
                            >
                              Bonavista Admin Dashboard
                            </Typography>
                          </Box>

                          {/* Shopping Illustration */}
                          <Box
                            sx={{
                              position: 'relative',
                              zIndex: 2,
                              mb: 3,
                              transition: 'transform 0.5s ease-in-out',
                              width: { xs: 220, sm: 280, md: 340, lg: 400 },
                              height: { xs: 110, sm: 140, md: 180, lg: 200 },
                              mx: 'auto',
                              '&:hover': {
                                transform: 'scale(1.05)'
                              }
                            }}
                          >
                            <DotLottieReact
                              src='https://lottie.host/e036819f-81b7-4c97-8a1b-fd27213ff44c/ITD7eS7fKt.lottie'
                              loop
                              autoplay
                              style={{ width: '100%', height: '100%' }} // Pastikan animasi mengikuti ukuran Box
                            />
                          </Box>
                        </Box>
                      </Slide>
                    </Grid>

                    {/* Right Side - Login Form */}
                    <Grid item xs={12} md={6} lg={5} mt={{ lg: 15, xl: 15 }}>
                      <Slide direction='left' in={mounted} timeout={1200}>
                        <Box
                          sx={{
                            height: '100%',
                            minHeight: { xs: 'auto', md: '600px', lg: '650px' },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            p: { xs: 3, sm: 4, md: 4, lg: 5 },
                            background: theme.palette.background.paper
                          }}
                        >
                          {/* Logo Section */}
                          <Zoom in={mounted} timeout={1400}>
                            <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 5 } }}>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  transition: 'transform 0.3s ease-in-out',
                                  '&:hover': {
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              >
                                <LogoLogin />
                              </Box>
                              <Typography
                                variant='h5'
                                sx={{
                                  mt: 2,
                                  fontWeight: 600,
                                  color: 'text.primary',
                                  fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.4rem' }
                                }}
                              >
                                Masuk ke Akun Anda
                              </Typography>
                              <Typography
                                variant='body2'
                                sx={{
                                  mt: 1,
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                }}
                              >
                                Masukkan kredensial Anda untuk mengakses dasbor Anda
                              </Typography>
                            </Box>
                          </Zoom>

                          {/* Login Form */}
                          <Fade in={mounted} timeout={1600}>
                            <Box
                              component='form'
                              onSubmit={doLogin}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: { xs: 2.5, sm: 3 },
                                mb: { xs: 3, sm: 4 },
                                mt: { lg: 15, xl: 15 }
                              }}
                              noValidate
                              autoComplete='off'
                            >
                              <TextField
                                autoFocus
                                fullWidth
                                label='Username'
                                size='medium'
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.12)}`
                                    },
                                    '&.Mui-focused': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}`
                                    }
                                  },
                                  '& .MuiInputLabel-root': {
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                  }
                                }}
                              />

                              <TextField
                                fullWidth
                                size='medium'
                                label='Password'
                                type={isPasswordShown ? 'text' : 'password'}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.12)}`
                                    },
                                    '&.Mui-focused': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}`
                                    }
                                  },
                                  '& .MuiInputLabel-root': {
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                  }
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position='end'>
                                      <IconButton
                                        size='small'
                                        edge='end'
                                        onClick={handleClickShowPassword}
                                        onMouseDown={e => e.preventDefault()}
                                        sx={{
                                          transition: 'transform 0.2s ease-in-out',
                                          '&:hover': {
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                      >
                                        <i className={isPasswordShown ? 'ri-eye-off-line' : 'ri-eye-line'} />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                              />

                              <CustomButton
                                type='submit'
                                loading={loading}
                                color='primary'
                                fullWidth
                                size='large'
                                sx={{
                                  py: { xs: 1.5, sm: 2 },
                                  mt: 5,
                                  fontSize: { xs: '0.9rem', sm: '1rem' },
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: 2,
                                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`
                                  }
                                }}
                              >
                                Login
                              </CustomButton>
                            </Box>
                          </Fade>

                          {/* Footer */}
                          <Fade in={mounted} timeout={1800}>
                            <Box sx={{ textAlign: 'center', mt: 'auto' }}>
                              <Typography
                                variant='body2'
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                }}
                              >
                                © {dayjs().format('YYYY')} {themeConfig.templateName}. All rights reserved.
                              </Typography>
                            </Box>
                          </Fade>
                        </Box>
                      </Slide>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Enhanced Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '8%',
          left: '3%',
          width: { xs: 60, sm: 80, md: 100 },
          height: { xs: 60, sm: 80, md: 100 },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          animation: 'pulse 6s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              opacity: 0.4
            },
            '50%': {
              transform: 'scale(1.3)',
              opacity: 0.8
            }
          }
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: { xs: 40, sm: 60, md: 80 },
          height: { xs: 40, sm: 60, md: 80 },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.06)} 0%, transparent 70%)`,
          animation: 'pulse 8s ease-in-out infinite reverse'
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          right: '2%',
          width: { xs: 30, sm: 40, md: 50 },
          height: { xs: 30, sm: 40, md: 50 },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.05)} 0%, transparent 70%)`,
          animation: 'pulse 10s ease-in-out infinite'
        }}
      />
    </Box>
  )
}

export default Login
